import React from 'react';

import {useLinkGeneration} from 'ui-library/hooks/useLinkGeneration';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface CopyLinkToAnchorButtonProps {
  /**
   * The anchor value to use in the link (e.g., UUID, section ID, etc.)
   */
  anchorValue?: string;

  /**
   * Hash parameter name for link generation.
   * @default anchor
   */
  hashParam?: string;

  /**
   * Title attribute for the button
   */
  title?: string;

  /**
   * Toast message to show when link is copied
   */
  toastText?: string;

  /**
   * CSS class name for styling
   */
  className?: string;

  /**
   * Icon name to display
   */
  icon?: BenchlingIconName;

  /**
   * Base URL for link generation.
   * @default Current page URL without hash
   */
  baseUrl?: string;
}

/**
 * Generic button component for copying links to anchor sections.
 *
 * Supports flexible anchor configurations for different types of navigation.
 * Uses 'anchor' as the default hash parameter for consistent URL structure.
 *
 * @param props Component props
 * @returns JSX element or null if no anchor value provided
 */
export default function CopyLinkToAnchorButton(props: CopyLinkToAnchorButtonProps): JSX.Element | null {
  const {
    anchorValue,
    hashParam,
    title = 'Copy link to this section',
    toastText,
    className = 'link-icon',
    icon = 'link',
    baseUrl,
  } = props;

  const {copyLinkToClipboard} = useLinkGeneration({
    hashParam,
    baseUrl,
    toastText,
  });

  // Don't render if no anchor value provided
  if (!anchorValue) {
    return null;
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        copyLinkToClipboard(anchorValue, e);
      }}
      title={title}
      className={className}
    >
      <BenchlingIcon icon={icon} />
    </span>
  );
}
